import {
  AuthUser,
  GetPaymentLinkRequest,
  LanguageNameSpaces,
  SubscriptionPlanType,
  UsageType,
} from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types";
import Text from "common/Typography/Text";
import ExtraQuotaText from "features/settings/Billing/ExtraQuotaText";
import PrimaryApiHelperTooltip from "features/settings/Billing/PrimaryApiHelperTooltip.tsx";
import React, { ReactNode } from "react";
import { typography } from "@shared/common/Themes/typography";
import {
  IS_APPLE_PAY,
  IS_GOOGLE_PAY,
  LOCAL_STORAGE_PAYMENTS_KEY,
} from "features/settings/common/constants.ts";
import { LocalStoragePaymentData } from "features/settings/common/types.ts";
import { isEqual, pickBy } from "lodash";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import SecureClientInviteHelperTooltip from "features/settings/Billing/SecureClientInviteHelperTooltip.tsx";
import InvoicingHelperTooltip from "features/settings/Billing/InvoicingHelperTooltip.tsx";
import WhitelabelHelperTooltip from "features/settings/Billing/WhitelabelHelperTooltip.tsx";

export type ExtraFeatureType = "invoicing" | "whitelabel";

/**
 * Builds the plan feature renderer; the new look passes a lighter weight for
 * the quota number.
 */
export const createQuotaPlanFeature =
  (numberWeight: number | string = typography.fontWeights.BOLD) =>
  (
    _usageType: UsageType | ExtraFeatureType | string,
    t: (
      key: TranslationKeys<LanguageNameSpaces.COMMON>,
      options?: Record<string, unknown>
    ) => string,
    numItemsText?: string,
    _titleObjectName?: string,
    showAdditionalQuota?: boolean,
    extraQuota?: number,
    planType?: SubscriptionPlanType
  ) => {
    let titleObjectName = _titleObjectName;
    if (!titleObjectName && _usageType === UsageType.SECURE_CLIENT_INVITE) {
      titleObjectName = t(
        getUsageTitleKey(UsageType.SECURE_CLIENT_INVITE, planType)
      );
    } else if (_usageType === "invoicing") {
      titleObjectName = t("tooltips.invoicing_title");
    } else if (_usageType === "whitelabel") {
      titleObjectName = t("tooltips.whitelabel_title");
    }
    let helper: ReactNode;
    if (_usageType === UsageType.CLIENT) {
      helper = <PrimaryApiHelperTooltip />;
    } else if (_usageType === UsageType.SECURE_CLIENT_INVITE) {
      helper = <SecureClientInviteHelperTooltip planType={planType} />;
    } else if (_usageType === "invoicing") {
      helper = <InvoicingHelperTooltip />;
    } else if (_usageType === "whitelabel") {
      helper = <WhitelabelHelperTooltip />;
    }
    return (
      <Text
        as="div"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0 4px",
          overflow: "hidden",
        }}
      >
        {numItemsText !== undefined && (
          <span
            className="plan-quota-number"
            style={{ fontWeight: numberWeight }}
          >
            {numItemsText}
          </span>
        )}
        {titleObjectName?.split(" ").map((word, i, arr) => {
          const isLast = i === arr.length - 1;
          if (isLast && !showAdditionalQuota && helper) {
            return (
              <span key={i} style={{ whiteSpace: "nowrap" }}>
                {word}
                {helper}
              </span>
            );
          }
          return <span key={i}>{word}</span>;
        })}
        {showAdditionalQuota && (
          <span className="plan-extra-quota" style={{ whiteSpace: "nowrap" }}>
            <ExtraQuotaText extraQuota={extraQuota} />
            {helper}
          </span>
        )}
      </Text>
    );
  };

export const getQuotaPlanFeature = createQuotaPlanFeature();

/** The plan feature renderer used by the new look (quota numbers at 500). */
export const getNewLookQuotaPlanFeature = createQuotaPlanFeature(500);

export const getParsedLocalStoragePayments = (): LocalStoragePaymentData[] => {
  let paymentsDataStr = localStorage?.getItem(LOCAL_STORAGE_PAYMENTS_KEY) || "";
  try {
    return JSON.parse(paymentsDataStr);
  } catch {}
  return [];
};

export const getParsedStorageRequest = (request: GetPaymentLinkRequest) => {
  const ignoreKeys = new Set(["invoice_id", "force_invoice", "time_expires"]);
  return pickBy(
    request,
    (value, key) => !!value && !ignoreKeys.has(key)
  ) as GetPaymentLinkRequest;
};

export const addLocalStoragePayment = (
  invoiceId: string,
  request: GetPaymentLinkRequest,
  timeExpires?: number
) => {
  const paymentsData = getParsedLocalStoragePayments();
  const parsedRequest = getParsedStorageRequest(request);
  const existingPaymentIndex = paymentsData.findIndex((paymentData) =>
    isEqual(getParsedStorageRequest(paymentData), parsedRequest)
  );
  const storagePaymentData = {
    ...parsedRequest,
    invoice_id: invoiceId,
    time_expires: timeExpires,
  };
  if (existingPaymentIndex > -1) {
    paymentsData[existingPaymentIndex] = storagePaymentData;
  } else {
    paymentsData.push(storagePaymentData);
  }
  localStorage?.setItem(
    LOCAL_STORAGE_PAYMENTS_KEY,
    JSON.stringify(paymentsData)
  );
};

export const removeLocalStoragePayment = (
  invoiceId?: string,
  request?: GetPaymentLinkRequest
) => {
  if (!invoiceId && !request) return;
  const parsedRequest = request ? getParsedStorageRequest(request) : undefined;
  const paymentsData = getParsedLocalStoragePayments().filter((paymentData) => {
    if (invoiceId) return paymentData.invoice_id !== invoiceId;
    else if (parsedRequest)
      return !isEqual(getParsedStorageRequest(paymentData), parsedRequest);
  });
  localStorage?.setItem(
    LOCAL_STORAGE_PAYMENTS_KEY,
    JSON.stringify(paymentsData)
  );
};

export const findLocalStoragePayment = (request: GetPaymentLinkRequest) => {
  const parsedRequest = getParsedStorageRequest(request);
  return getParsedLocalStoragePayments().find((paymentData) =>
    isEqual(getParsedStorageRequest(paymentData), parsedRequest)
  );
};

export const calcApiSlotsPrice = (
  numSlots: number,
  apiSlots: Record<number, number> | undefined
): { totalPrice: number; breakdown: { slots: number; price: number }[] } => {
  if (!apiSlots || numSlots === 0) {
    return {
      totalPrice: 0,
      breakdown: [{ slots: numSlots, price: 0 }],
    };
  }
  const MIN_TIER = 10;
  const effectiveSlots = Math.max(numSlots, MIN_TIER);
  const sortedTiers = Object.keys(apiSlots)
    .map(Number)
    .sort((a, b) => b - a);
  const matchedTier =
    sortedTiers.find((tier) => tier <= effectiveSlots) ??
    sortedTiers[sortedTiers.length - 1];
  const unitPrice = apiSlots[matchedTier] ?? 0;
  const totalPrice = numSlots * unitPrice;
  return {
    totalPrice,
    breakdown: [{ slots: numSlots, price: totalPrice }],
  };
};

export const getIsDifferentPaymentMethod = (user?: AuthUser) => {
  return (
    (IS_GOOGLE_PAY && !user?.isGooglePaySubscription()) ||
    (IS_APPLE_PAY && !user?.isApplePaySubscription())
  );
};
